import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCombinedStore } from "../../store";
import * as AlfredoStyles from "../../styles/layouts/layouts";
import * as AlfredoInterfaces from "../../interfaces";
import * as AlfredoComponents from "../../components";

const TopBar: React.FC<AlfredoInterfaces.TopBarProps> = ({
  onLeftBarClick,
  onRightBarClick,
  userInfo,
  organization,
}) => {
  const navigate = useNavigate();
  /* A foto do topo pode ter URL morta no banco; ver o onError abaixo. */
  const [fotoFalhou, setFotoFalhou] = useState(false);
  const [activeIcon, setActiveIcon] = useState<string>("menu");
  const themeZustand = useCombinedStore((state) => state.themeZustand);
  const toggleTheme = useCombinedStore((state) => state.toggleTheme);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const removeAuthUserZustand = useCombinedStore(
    (state) => state.removeAuthUserZustand
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLeftBarClick = () => {
    setActiveIcon("menu");
    onLeftBarClick();
  };

  const handleRightBarClick = () => {
    setActiveIcon("storefront");
    onRightBarClick();
  };

  const handleLogout = () => {
    localStorage.clear();
    document.cookie =
      "refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    removeAuthUserZustand();
    navigate("/login");
  };

  return (
    <>
      <link
        href="https://fonts.googleapis.com/icon?family=Material+Icons"
        rel="stylesheet"
      />
      <AlfredoStyles.SidebarTitle>alfredo central</AlfredoStyles.SidebarTitle>

      <AlfredoStyles.SearchContainer>
        <AlfredoComponents.SearchInput />
      </AlfredoStyles.SearchContainer>

      <AlfredoStyles.OptionsContainer>
        <AlfredoStyles.StyledTooltip title="Alterar o tema">
          <AlfredoStyles.IconContainer onClick={() => toggleTheme()}>
            <AlfredoStyles.StyledIcon>
              <AlfredoStyles.MaterialIcons>
                {themeZustand.title === "light" ? "brightness_7" : "nightlight"}
              </AlfredoStyles.MaterialIcons>
            </AlfredoStyles.StyledIcon>
          </AlfredoStyles.IconContainer>
        </AlfredoStyles.StyledTooltip>

        <AlfredoStyles.StyledTooltip title="Abrir menu lateral">
          <AlfredoStyles.MenuContainer>
            <AlfredoComponents.Menu onClick={handleLeftBarClick} />
          </AlfredoStyles.MenuContainer>
        </AlfredoStyles.StyledTooltip>

        <AlfredoStyles.StyledTooltip title="Configurações da conta">
          <AlfredoStyles.StyledIconButton
            onClick={handleClick}
            /* Nome explícito porque o conteúdo do botão é só imagem decorativa
               (alt vazio) ou uma ligatura de ícone; sem ele o leitor de tela
               anuncia "botão" e mais nada. */
            aria-label="Configurações da conta"
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            sx={{
              color: "unset",
              "&:hover": {
                backgroundColor: "unset",
              },
            }}
          >
            {userInfo.userLogoUrl && !fotoFalhou ? (
              <AlfredoStyles.ImgContainer>
                <AlfredoStyles.StyledImg
                  src={userInfo.userLogoUrl}
                  alt=""
                  /*
                   * O `onError` é o que evita o ícone de imagem partida.
                   *
                   * A URL vem do banco e pode estar morta — foi o caso em
                   * 03/09/2026: a foto do dono apontava para o Backblaze B2
                   * (o storage anterior ao R2), gravada em 17/12/2025, e o
                   * objeto respondia 404. Sem tratar a falha, o navegador
                   * desenha o quadro partido com o texto alternativo do lado,
                   * enquanto o ramo do `account_circle` logo abaixo — que
                   * existe exatamente para "sem foto" — nunca era alcançado.
                   *
                   * URL morta e ausência de foto são a MESMA coisa para quem
                   * olha a tela; agora as duas caem no mesmo ícone.
                   */
                  onError={() => setFotoFalhou(true)}
                />
              </AlfredoStyles.ImgContainer>
            ) : (
              <AlfredoStyles.IconContainer>
                <AlfredoStyles.StyledIcon>
                  <AlfredoStyles.MaterialIcons>
                    account_circle
                  </AlfredoStyles.MaterialIcons>
                </AlfredoStyles.StyledIcon>
              </AlfredoStyles.IconContainer>
            )}
          </AlfredoStyles.StyledIconButton>
        </AlfredoStyles.StyledTooltip>

        <AlfredoStyles.DropdownMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <AlfredoStyles.StyledMenuItem onClick={handleClose}>
            <AlfredoStyles.StyledMenuIcon>person</AlfredoStyles.StyledMenuIcon>
            Minha Conta
          </AlfredoStyles.StyledMenuItem>
          <AlfredoStyles.StyledMenuItem onClick={handleClose}>
            <AlfredoStyles.StyledMenuIcon>
              language
            </AlfredoStyles.StyledMenuIcon>
            Idioma: Português
          </AlfredoStyles.StyledMenuItem>
          <AlfredoStyles.StyledMenuItem onClick={handleClose}>
            <AlfredoStyles.StyledMenuIcon>
              support_agent
            </AlfredoStyles.StyledMenuIcon>
            Ajuda e Suporte
          </AlfredoStyles.StyledMenuItem>
          <AlfredoStyles.StyledMenuItem onClick={handleLogout}>
            <AlfredoStyles.StyledMenuIcon>logout</AlfredoStyles.StyledMenuIcon>
            Encerrar sessão
          </AlfredoStyles.StyledMenuItem>
        </AlfredoStyles.DropdownMenu>

        <AlfredoStyles.StyledTooltip title="Visualizar Lojas">
          {organization?.organizationLogoUrl ? (
            <AlfredoStyles.ImgContainer onClick={handleRightBarClick}>
              <AlfredoStyles.StyledImg
                src={organization.organizationLogoUrl}
                alt="foto da empresa."
              />
            </AlfredoStyles.ImgContainer>
          ) : (
            <AlfredoStyles.IconContainer onClick={handleRightBarClick}>
              <AlfredoStyles.StyledIcon>
                <AlfredoStyles.MaterialIcons>
                  storefront
                </AlfredoStyles.MaterialIcons>
              </AlfredoStyles.StyledIcon>
            </AlfredoStyles.IconContainer>
          )}
        </AlfredoStyles.StyledTooltip>
      </AlfredoStyles.OptionsContainer>
    </>
  );
};

export default TopBar;
